// Typed fetch wrapper around the FastAPI backend.
// Auto-injects Authorization: Bearer <token>; throws ApiError on non-2xx.
import { getToken, clearToken } from "@/lib/auth";
export class ApiError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = "ApiError";
    }
}
async function request(path, init = {}) {
    const headers = {
        "Content-Type": "application/json",
        ...init.headers,
    };
    if (!init.skipAuth) {
        const token = getToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(path, { ...init, headers });
    if (res.status === 401) {
        clearToken();
        throw new ApiError(401, "unauthenticated");
    }
    if (!res.ok) {
        let detail = res.statusText;
        try {
            const body = await res.json();
            detail = body.detail ?? detail;
        }
        catch {
            /* ignore */
        }
        throw new ApiError(res.status, detail);
    }
    if (res.status === 204)
        return undefined;
    // 202 endpoints (e.g. messages POST) may return small JSON like {accepted:true}
    return res.json();
}
export const authApi = {
    status: () => request("/api/auth/status", { skipAuth: true }),
    signup: (email, password) => request("/api/auth/signup", {
        method: "POST",
        body: JSON.stringify({ email, password }),
        skipAuth: true,
    }),
    login: (email, password, device_label) => request("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, device_label }),
        skipAuth: true,
    }),
    me: () => request("/api/auth/me"),
    logout: () => request("/api/auth/logout", { method: "POST" }),
};
// ---- Projects ------------------------------------------------------------
export const projectsApi = {
    list: () => request("/api/projects"),
    create: (name, workspace_path) => request("/api/projects", {
        method: "POST",
        body: JSON.stringify({ name, workspace_path }),
    }),
    getDefault: () => request("/api/projects/default"),
    get: (id) => request(`/api/projects/${encodeURIComponent(id)}`),
    updateSettings: (id, patch) => request(`/api/projects/${encodeURIComponent(id)}/settings`, {
        method: "PATCH",
        body: JSON.stringify(patch),
    }),
    remove: (id) => request(`/api/projects/${encodeURIComponent(id)}`, {
        method: "DELETE",
    }),
    // Async delete with progress UI. Mirrors sessionApi.startDelete.
    // The step list is 7 * N where N is the number of sessions in the
    // project. The project entry is removed from the local list
    // optimistically the moment the user confirms.
    startDelete: (id) => request(`/api/projects/${encodeURIComponent(id)}/delete`, { method: "POST" }),
    deleteJobStatus: (id, jobId) => request(`/api/projects/${encodeURIComponent(id)}/delete-jobs/${encodeURIComponent(jobId)}`),
    cancelDelete: (id, jobId) => request(`/api/projects/${encodeURIComponent(id)}/delete-jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" }),
};
export const adminApi = {
    processes: () => request("/api/admin/processes"),
    killProcess: (pid) => request(`/api/admin/processes/${pid}`, { method: "DELETE" }),
    services: () => request("/api/admin/services"),
    users: () => request("/api/admin/users"),
    deleteUser: (userId) => request(`/api/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" }),
    resetUserPassword: (userId, newPassword) => request(`/api/admin/users/${encodeURIComponent(userId)}/password`, {
        method: "POST",
        body: JSON.stringify({ new_password: newPassword }),
    }),
    // Update instance-wide display settings (root only). Returns the full
    // current settings so the caller can refresh its cache in one shot.
    updateSettings: (patch) => request("/api/admin/settings", {
        method: "PATCH",
        body: JSON.stringify(patch),
    }),
};
export const settingsApi = {
    get: () => request("/api/settings"),
};
export const providersApi = {
    list: () => request("/api/admin/providers"),
    setActive: (provider, model) => request("/api/admin/providers/active", {
        method: "PATCH",
        body: JSON.stringify({ provider, model }),
    }),
    setKey: (providerId, api_key, base_url) => request(`/api/admin/providers/${encodeURIComponent(providerId)}/key`, {
        method: "PUT",
        body: JSON.stringify({ api_key, base_url: base_url ?? null }),
    }),
    deleteKey: (providerId) => request(`/api/admin/providers/${encodeURIComponent(providerId)}/key`, { method: "DELETE" }),
    test: (provider, model) => request("/api/admin/providers/test", {
        method: "POST",
        body: JSON.stringify({ provider, model }),
    }),
};
export const pricingApi = {
    list: () => request("/api/admin/pricing"),
    set: (model, body) => request(`/api/admin/pricing/${encodeURIComponent(model)}`, { method: "PUT", body: JSON.stringify(body) }),
    delete: (model) => request(`/api/admin/pricing/${encodeURIComponent(model)}`, { method: "DELETE" }),
};
export const pathsApi = {
    common: () => request("/api/paths/common"),
    browse: (cwd) => request(`/api/paths/browse${cwd ? `?cwd=${encodeURIComponent(cwd)}` : ""}`),
};
export const deployedAppsApi = {
    list: () => request("/api/deployed-apps"),
    delete: (slug) => request(`/api/deployed-apps/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    // Pause / resume. Static apps (the only kind in v1) just swap a
    // directory on disk; v1.1 fullstack apps will additionally start/stop
    // a per-app systemd unit (transparent to the UI).
    start: (slug) => request(`/api/deployed-apps/${encodeURIComponent(slug)}/start`, { method: "POST" }),
    stop: (slug) => request(`/api/deployed-apps/${encodeURIComponent(slug)}/stop`, { method: "POST" }),
    state: (slug) => request(`/api/deployed-apps/${encodeURIComponent(slug)}/state`),
    // Grouped by source session for the Settings page.
    mine: () => request("/api/users/me/deployed-apps"),
    // Deploy is per-session — convenience method lives on sessionApi below.
};
// ---- Sessions ------------------------------------------------------------
// All sessions for a project, newest-first. The server returns the full
// list — no pagination — so the SessionList page and the Workspace
// sidebar both get every row in one round trip.
export const sessionsApi = {
    list: (projectId) => request(`/api/projects/${encodeURIComponent(projectId)}/sessions`),
    create: (projectId, name) => request(`/api/projects/${encodeURIComponent(projectId)}/sessions`, { method: "POST", body: JSON.stringify({ name }) }),
    get: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}`),
    rename: (sessionId, newName) => request(`/api/sessions/${encodeURIComponent(sessionId)}`, {
        method: "PATCH",
        body: JSON.stringify({ new_name: newName }),
    }),
    /** Like `rename` but also returns the X-Was-Suffixed / X-Actual-Name
     *  headers so the UI can show a toast when the server auto-suffixed
     *  the name to avoid a collision. */
    renameWithSufStatus: async (sessionId, newName) => {
        const headers = {
            "Content-Type": "application/json",
        };
        const token = getToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
            method: "PATCH",
            headers,
            body: JSON.stringify({ new_name: newName }),
        });
        if (res.status === 401) {
            clearToken();
            throw new ApiError(401, "unauthenticated");
        }
        if (!res.ok) {
            let detail = res.statusText;
            try {
                const body = await res.json();
                detail = body.detail ?? detail;
            }
            catch {
                /* ignore */
            }
            throw new ApiError(res.status, detail);
        }
        const session = (await res.json());
        const wasSuffixed = res.headers.get("X-Was-Suffixed") === "true";
        const actualName = res.headers.get("X-Actual-Name") ?? session.name;
        return { session, wasSuffixed, actualName };
    },
    remove: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}`, {
        method: "DELETE",
    }),
};
// ---- Messages + events ---------------------------------------------------
export const sessionApi = {
    messages: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/messages`),
    post: (sessionId, content) => request(`/api/sessions/${encodeURIComponent(sessionId)}/messages`, { method: "POST", body: JSON.stringify({ content }) }),
    events: (sessionId, since) => {
        const q = since != null ? `?since=${since}` : "";
        return request(`/api/sessions/${encodeURIComponent(sessionId)}/events${q}`);
    },
    git: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/git`),
    push: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/push`, { method: "POST" }),
    cancel: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/cancel`, { method: "POST" }),
    compact: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/compact`, { method: "POST" }),
    // Wire-level LLM call trace. Server keeps last 50 in an in-memory
    // ring buffer; fetched on panel open (not polled).
    llmTrace: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/llm-trace`),
    // Wipe the in-memory trace buffer for this session.
    clearLlmTrace: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/llm-trace`, { method: "DELETE" }),
    // Promote a session's built dist/ to a permanent subdomain URL.
    // Returns 202 Accepted with a {job_id, slug, url, placeholder_app}
    // envelope; the actual work runs in a background task and the
    // client polls deployJobStatus() for per-step progress. Sync 4xx
    // errors (no built dist, bad sub-app folder, slug collision) still
    // come back as the corresponding status code from this same call
    // (no job is created for those).
    //   slug         — leftmost label of the public URL. Server slugifies.
    //   project_dir  — usually set automatically from `detectedDist()`.
    deploy: (sessionId, opts = {}, init) => request(`/api/sessions/${encodeURIComponent(sessionId)}/deploy`, {
        method: "POST",
        body: JSON.stringify({
            slug: opts.slug ?? null,
            project_dir: opts.project_dir ?? null,
        }),
        ...(init?.signal ? { signal: init.signal } : {}),
    }),
    // Poll for the per-step status of an in-flight or recently-finished
    // deploy. 404 if the job_id is unknown OR not owned by the caller
    // (the server intentionally doesn't differentiate). 11 entries in
    // `steps` in a fixed order so the UI checklist is stable.
    deployJobStatus: (sessionId, jobId, init) => request(`/api/sessions/${encodeURIComponent(sessionId)}/deploy-jobs/${encodeURIComponent(jobId)}`, init?.signal ? { signal: init.signal } : {}),
    // Cooperative cancel of an in-flight deploy. Idempotent — returns
    // {ok: false, reason: "job not running"} if the job is already done.
    cancelDeployJob: (sessionId, jobId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/deploy-jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" }),
    // Scan the session workspace for built dist/ folders. The deploy
    // dialog calls this on open to pre-fill (and lock) the Sub-app
    // folder field. Returns all candidates sorted newest-first; the
    // `auto_pick` is what the deploy endpoint would use by default.
    detectedDist: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/detected-dist`),
    // Just the deploys made from THIS session — chat strip renders these
    // as pills with Open / Delete controls.
    deployedApps: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/deployed-apps`),
    // Async delete with progress UI. Returns 202 with a {job_id, ...}
    // envelope; the actual cleanup runs in a background task. The
    // sidebar removes the entry optimistically the moment the user
    // confirms — this method just kicks off the server-side teardown.
    startDelete: (sessionId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/delete`, { method: "POST" }),
    deleteJobStatus: (sessionId, jobId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/delete-jobs/${encodeURIComponent(jobId)}`),
    cancelDelete: (sessionId, jobId) => request(`/api/sessions/${encodeURIComponent(sessionId)}/delete-jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" }),
};
